/**
 * ERP 글꼴 카탈로그 — 무료·상업적 이용 가능(SIL OFL 등)만
 * - 기관 기본 글꼴: appearance.fontFamilyId
 * - 글자 크기: html font-size(rem 루트). Ctrl+휠 확대와 달리 레이아웃 px는 유지
 */

export const FONT_SIZE_BASE_PX = 13;

/** 디자인 토큰 단계 (13px = 1rem 기준) */
export const FONT_SIZE_STEPS_PX = {
  xxs: 10,
  xs: 11,
  sm: 12,
  md: 13,
  lg: 15,
  xl: 20,
  xxl: 24,
};

/** 사용자가 고를 수 있는 본문(md) 크기 */
export const FONT_SIZE_PRESETS = [
  { px: 12, id: 'sm', label: '작음' },
  { px: 13, id: 'md', label: '기본' },
  { px: 14, id: 'lg', label: '조금 크게' },
  { px: 15, id: 'xl', label: '크게' },
  { px: 16, id: 'xxl', label: '더 크게' },
];

export const FONT_SIZE_MIN_PX = FONT_SIZE_PRESETS[0].px;
export const FONT_SIZE_MAX_PX = FONT_SIZE_PRESETS[FONT_SIZE_PRESETS.length - 1].px;

const SYSTEM_FALLBACK =
  "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Noto Sans KR', sans-serif";

/**
 * @typedef {{
 *   id: string,
 *   label: string,
 *   family: string,
 *   license: string,
 *   description: string,
 *   cssUrl?: string | null,
 * }} ErpFontDef
 */

/** @type {ErpFontDef[]} */
export const ERP_FONTS = [
  {
    id: 'pretendard',
    label: 'Pretendard',
    family: 'Pretendard',
    license: 'SIL OFL 1.1',
    description: '현재 ERP 기본. 한글·숫자 가독성이 균형 잡힌 산세리프',
    cssUrl: null,
  },
  {
    id: 'pretendardGov',
    label: 'Pretendard GOV',
    family: 'Pretendard GOV',
    license: 'SIL OFL 1.1',
    description: '공공·행정 문서용 Pretendard 파생. 본문 밀도가 조금 더 단정함',
    cssUrl:
      'https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/packages/pretendard-gov/dist/web/static/pretendard-gov.min.css',
  },
  {
    id: 'suit',
    label: 'SUIT',
    family: 'SUIT',
    license: 'SIL OFL 1.1',
    description: '본문·UI 모두에 무난. 화면이 조금 더 또렷하게 보임',
    cssUrl: 'https://cdn.jsdelivr.net/gh/sun-typeface/SUIT@2.0.5/fonts/static/woff2/SUIT.css',
  },
  {
    id: 'wantedSans',
    label: 'Wanted Sans',
    family: 'Wanted Sans',
    license: 'SIL OFL 1.1',
    description: '그리드 숫자·라틴 폭이 고른 편. 표 위주 화면에 유리',
    cssUrl:
      'https://cdn.jsdelivr.net/gh/wanteddev/wanted-sans@v1.0.3/packages/wanted-sans/fonts/webfonts/static/complete/WantedSans.min.css',
  },
  {
    id: 'ibmPlexSansKr',
    label: 'IBM Plex Sans KR',
    family: 'IBM Plex Sans KR',
    license: 'SIL OFL 1.1',
    description: 'IBM Plex 한글. 코드·숫자와 본문이 한 계열로 맞음',
    cssUrl: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+KR:wght@400;500;600;700&display=swap',
  },
  {
    id: 'notoSansKr',
    label: 'Noto Sans KR',
    family: 'Noto Sans KR',
    license: 'SIL OFL 1.1',
    description: 'Google Noto. 기기 상관없이 글리프 커버리지가 넓음',
    cssUrl: 'https://fonts.googleapis.com/css2?family=Noto+Sans+KR:wght@400;500;600;700&display=swap',
  },
];

export const DEFAULT_FONT_ID = 'pretendard';

/** @param {string} [id] */
export function getErpFont(id) {
  return ERP_FONTS.find((f) => f.id === id) || ERP_FONTS[0];
}

/** @param {string} [id] */
export function fontFamilyStack(id) {
  const def = getErpFont(id);
  if (def.family === 'Pretendard') {
    return `'Pretendard', ${SYSTEM_FALLBACK}`;
  }
  return `'${def.family}', 'Pretendard', ${SYSTEM_FALLBACK}`;
}

export function fontSizeTokensRem(basePx = FONT_SIZE_BASE_PX) {
  /** @type {Record<string, string>} */
  const out = {};
  Object.keys(FONT_SIZE_STEPS_PX).forEach((key) => {
    const px = FONT_SIZE_STEPS_PX[key];
    out[key] = `${Math.round((px / basePx) * 10000) / 10000}rem`;
  });
  return out;
}

/** @param {unknown} value */
export function normalizeFontSizeRootPx(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return FONT_SIZE_BASE_PX;
  let best = FONT_SIZE_PRESETS[0].px;
  let bestDist = Math.abs(n - best);
  FONT_SIZE_PRESETS.forEach((p) => {
    const d = Math.abs(n - p.px);
    if (d < bestDist) {
      best = p.px;
      bestDist = d;
    }
  });
  return best;
}

/** @param {number} px */
export function fontSizePresetByPx(px) {
  const n = normalizeFontSizeRootPx(px);
  return FONT_SIZE_PRESETS.find((p) => p.px === n) || FONT_SIZE_PRESETS[1];
}

/** @param {number} px @param {1|-1} dir */
export function stepFontSizeRootPx(px, dir) {
  const cur = normalizeFontSizeRootPx(px);
  const idx = FONT_SIZE_PRESETS.findIndex((p) => p.px === cur);
  const next = FONT_SIZE_PRESETS[idx + dir];
  return next ? next.px : cur;
}

/**
 * @param {string} [id]
 */
export function ensureErpFontLoaded(id) {
  if (typeof document === 'undefined') return;
  const def = getErpFont(id);
  if (!def.cssUrl) return;
  if (document.querySelector(`link[data-erp-font="${def.id}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = def.cssUrl;
  link.dataset.erpFont = def.id;
  document.head.appendChild(link);
}

export function ensureAllErpFontsLoaded() {
  ERP_FONTS.forEach((f) => ensureErpFontLoaded(f.id));
}

/**
 * @param {{ fontFamilyId?: string, fontSizeRootPx?: number }} from
 * @param {{ fontFamilyId?: string, fontSizeRootPx?: number }} to
 */
export function describeTypographyDelta(from, to) {
  const fromFont = getErpFont(from?.fontFamilyId);
  const toFont = getErpFont(to?.fontFamilyId);
  const fromPx = normalizeFontSizeRootPx(from?.fontSizeRootPx);
  const toPx = normalizeFontSizeRootPx(to?.fontSizeRootPx);
  const pct = fromPx === 0 ? 0 : Math.round(((toPx / fromPx - 1) * 1000) / 10);
  const fontChanged = fromFont.id !== toFont.id;
  const sizeChanged = fromPx !== toPx;
  if (!fontChanged && !sizeChanged) {
    return '아직 변경이 없습니다. 글꼴이나 크기를 바꾸면 왼쪽(현재)과 오른쪽(변경 후)이 달라집니다.';
  }
  const bits = [];
  if (fontChanged) bits.push(`글꼴 ${fromFont.label} → ${toFont.label}`);
  if (sizeChanged) {
    const sign = pct > 0 ? '+' : '';
    bits.push(`본문 ${fromPx}px → ${toPx}px (${sign}${pct}%)`);
  }
  bits.push('버튼·여백·아이콘 크기는 그대로입니다');
  return bits.join(' · ');
}
